import { ModuleLabel } from '../module-label';
import { GraphExample } from '../content-creator/graph-model';
import { GraphExampleContent } from '../graph-example-content';
import { markdownText } from '../../general/markdown';
import Config from "../../general/config";
import '../../assets/view-managed-module-dialog.css';
import { fieldWeight } from '../../general/field-weight';
import { Dialog, DialogHandle } from './dialog';
import { Button } from "primereact/button";
import {
  FragmentActionDto,
  ManagedFragmentDto,
  ModuleDto,
  ValidateFormPayloadDto,
  AiRequestDto,
  AiResponseDto,
  MessageDto,
  AiValidationResponseDataDto,
  isMessageDto,
  isAiValidationResponseDto
} from "../../general/dtos";
import { ProgressSpinner } from "primereact/progressspinner";
import { HtmlViewer, HtmlViewerHandle } from "../htmlViewer";
import { JSX, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createModulePositionWriter } from '../../general/module-position';
import { toggleFullscreen } from "../../general/browser";
import { InstructionsDialog } from "./view-module-instructions-dialog";
import {
  buildErrorMessage,
  renderFragmentHtml,
  UserAnswerVerificationResult,
  verifyUserAnswers
} from "../content-viewer/rendering-helpers";
import { GeneralErrorDialog } from "./error-dialog";
import { FormVerificationDialog } from "../content-viewer/form-verification-results-dialog";
import { TieredMenu } from "primereact/tieredmenu";
import { useApi } from "../../providers/api-provider";
import { Api } from "../../general/api";
import { useAiStore } from "../zustand-stores";
import { AIExplainDialog } from "./ai-explain-dialog";
import { GeneralInfoDialog } from "./info-dialog";
import { getNonStreamingResponse } from "../../general/ai-mock";
import { isArray } from "lodash";
import { Dropdown } from "primereact/dropdown";
import { AiAssistantMode, AiWorkspaceDialog } from "./ai-workspace-dialog";
import { EUROPEAN_LANGUAGES } from "../../general/utilities";
import { Toast } from "primereact/toast";
import { readableAnswer } from '../content-viewer/fragment-answers';
import { includeFreeTextResults, isAiQueryableResult, needsAutomaticAiVerification, normalizeAiChoiceResults } from '../content-viewer/form-ai-validation';
import { hasExpectedAnswer, isMultiChoiceType, selectionKeys } from '../content-viewer/multi-choice-fields';
import { changedFieldAnswers, fieldAnswers, hasFilledAnswer, iframeModuleAnswers } from '../content-viewer/saved-answers';
import { ModulePreviewViewport } from '../module-preview-viewport';
import { CompareAnswersDialog } from './compare-answers-dialog';
import { LearnerFormComparison } from '../../general/learner-form-comparisons';

type ViewManagedModuleDialogProps = {
  visible: boolean;
  contentLoading: boolean;
  fullModule: ModuleDto | null;
  isTestMode: boolean;
  courseKey?: string | null;
  isReviewMode?: boolean;
  initialFragmentKey?: string | null;
  startFromBeginning?: boolean;
  onPositionViewed?: (fragmentKey: string) => void;
  allowFragmentSelection?: boolean;
  onClose: () => void;
  onMessage?: (kind: string, data: any) => Promise<void>;
};

const USE_MOCK: boolean = false;// Config.env === "local";
const USE_AI_ASSISTANT: boolean = true;

type AiAssistantEntry = {
  content: string;
  language: string;
};

type AiAssistantResponses = {
  explanation: AiAssistantEntry | null;
  fragment_translation: AiAssistantEntry | null;
  response_translation: AiAssistantEntry | null;
  selection_explanation: AiAssistantEntry | null;
};

type ToolbarLayoutMode = "mobile" | "compact" | "desktop";

const getToolbarLayoutMode = (width: number): ToolbarLayoutMode => {
  if (width <= 640) return "mobile";
  if (width <= 900) return "compact";
  return "desktop";
};

export function ViewManagedModuleDialog({
  visible,
  contentLoading,
  fullModule,
  isTestMode,
  courseKey = null,
  isReviewMode = false,
  initialFragmentKey = null,
  startFromBeginning = false,
  onPositionViewed,
  allowFragmentSelection = false,
  onClose,
  onMessage
}: ViewManagedModuleDialogProps) {
  const { preferredLanguage, responseCache, setCacheValue } = useAiStore();
  const api: Api | null = useApi();
  const dialogRef = useRef<DialogHandle>(null);
  const dialogContainerRef = useRef<HTMLDivElement | null>(null);
  const formRef = useRef<HtmlViewerHandle>(null);
  const toastRef = useRef<Toast>(null);

  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [previewWidth, setPreviewWidth] = useState<number | null>(null);
  const [previewControlsTarget, setPreviewControlsTarget] = useState<HTMLDivElement | null>(null);
  const [instructionsVisible, setInstructionsVisible] = useState<boolean>(false);
  const [selectedFragment, setSelectedFragment] = useState<ManagedFragmentDto>({} as ManagedFragmentDto);
  const [userInputs, setUserInputs] = useState<Record<string, Record<string, any>>>({});
  const [comparisonData, setComparisonData] = useState<{ context: string; items: LearnerFormComparison[] } | null>(null);
  const [comparisonError, setComparisonError] = useState('');
  const [comparisonRetry, setComparisonRetry] = useState(0);
  const [comparisonLoading, setComparisonLoading] = useState(false);
  const [comparisonVisible, setComparisonVisible] = useState(false);
  const [comparisonValues, setComparisonValues] = useState<Record<string, any>>({});
  const comparisonContext = JSON.stringify([fullModule?.module_key, selectedFragment.key, courseKey]);
  const hasCompareAction = Boolean(selectedFragment.actions?.some(action => action.type === 'compareAnswers'));
  const availableComparisons = comparisonData?.context === comparisonContext ? comparisonData.items : [];
  useEffect(() => {
    let cancelled = false;
    setComparisonVisible(false); setComparisonData(null); setComparisonError(''); setComparisonLoading(false);
    if (!visible || isTestMode || !api || !fullModule?.module_key || !selectedFragment.key || !hasCompareAction) return;
    setComparisonLoading(true);
    api.getMyFormComparisons(fullModule.module_key, selectedFragment.key, courseKey).then(response => {
      if (!cancelled) setComparisonData({ context: comparisonContext, items: response.data });
    }).catch(error => {
      if (!cancelled) setComparisonError(error?.response?.data?.message ?? 'Unable to load answer comparisons. Please try again.');
    }).finally(() => { if (!cancelled) setComparisonLoading(false); });
    return () => { cancelled = true; };
  }, [visible, isTestMode, api, fullModule?.module_key, selectedFragment.key, courseKey, comparisonContext, comparisonRetry, hasCompareAction]);
  const userInputsRef = useRef<Record<string, Record<string, any>>>({});
  const answerBaselinesRef = useRef<Record<string, Record<string, any>>>({});
  const navigationBusyRef = useRef(false);
  const [answersReadyFor, setAnswersReadyFor] = useState<string | null>(null);
  const [answersLoadError, setAnswersLoadError] = useState('');
  const [answersRetry, setAnswersRetry] = useState(0);
  const [answerSaveError, setAnswerSaveError] = useState('');
  const [isSavingAnswers, setIsSavingAnswers] = useState(false);
  const [lockedFragments, setLockedFragments] = useState<string[]>([]);
  const [savedAnswersReadOnly, setSavedAnswersReadOnly] = useState(false);
  const [singleUseConfirmationVisible, setSingleUseConfirmationVisible] = useState(false);
  const confirmationResolverRef = useRef<((confirmed: boolean) => void) | null>(null);

  const resolveSingleUseConfirmation = (confirmed: boolean) => {
    setSingleUseConfirmationVisible(false);
    confirmationResolverRef.current?.(confirmed);
    confirmationResolverRef.current = null;
  };

  useEffect(() => () => { confirmationResolverRef.current?.(false); }, []);
  useEffect(() => { setAnswerSaveError(''); }, [visible, fullModule?.module_key]);

  useEffect(() => {
    let cancelled = false;
    userInputsRef.current = {};
    answerBaselinesRef.current = {};
    setUserInputs({});
    setAnswersReadyFor(null);
    setAnswersLoadError('');
    setLockedFragments([]);
    setSavedAnswersReadOnly(false);
    if (!visible || !fullModule?.module_key) return;
    const key = fullModule.module_key;
    if (isTestMode) {
      setAnswersReadyFor(key);
    } else if (api) {
      api.getMyModuleAnswers(key).then(response => {
        if (cancelled) return;
        const restored = iframeModuleAnswers(response.answers);
        userInputsRef.current = restored;
        setUserInputs(restored);
        setLockedFragments(response.lockedFragments ?? []);
        setSavedAnswersReadOnly(response.readOnly ?? false);
        setAnswersReadyFor(key);
      }).catch(error => {
        if (!cancelled) setAnswersLoadError(error?.response?.data?.message ?? 'Unable to load your saved answers. Please try again.');
      });
    }
    return () => { cancelled = true; };
  }, [visible, fullModule?.module_key, isTestMode, api, answersRetry]);

  const answersLoading = !isTestMode && answersReadyFor !== fullModule?.module_key;
  const isFragmentLocked = !isTestMode && Boolean(selectedFragment.fillOnce) && lockedFragments.includes(selectedFragment.key);
  const isModuleReadOnly = !isTestMode && (isReviewMode || savedAnswersReadOnly);
  const isAnswerReadOnly = isModuleReadOnly || isFragmentLocked;
  const moduleKey = fullModule?.module_key;
  const recordPosition = useMemo(() => createModulePositionWriter(fragmentKey =>
    api!.saveMyModulePosition(moduleKey!, fragmentKey)), [api, moduleKey]);

  const renderSelectedFragment = () => renderFragmentHtml(selectedFragment.html, selectedFragment.formFields, {
    fragments: fullModule?.fragments ?? [], currentKey: selectedFragment.key, answers: userInputs
  });
  const [showValidationErrorDialog, setShowValidationErrorDialog] = useState<boolean>(false);
  const [validationMessage, setValidationMessage] = useState<string>("");
  const [verificationResults, setVerificationResults] = useState<UserAnswerVerificationResult[] | null>(null);
  const [verificationResultsTo, setVerificationResultsTo] = useState<string>("");
  const [verificationResultsPayload, setVerificationResultsPayload] = useState<ValidateFormPayloadDto | null>(null);
  const [verificationAction, setVerificationAction] = useState<FragmentActionDto | null>(null);
  const [aiResponse, setAiResponse] = useState<string | AiValidationResponseDataDto>("");
  const [isAiLoading, setIsAiLoading] = useState<boolean>(false);
  const [isTranslating, setIsTranslating] = useState<boolean>(false);
  const [displayedLanguage, setDisplayedLanguage] = useState<string>("English");
  const [isAiDialogVisible, setIsAiDialogVisible] = useState<boolean>(false);
  const [isAiAssistantVisible, setIsAiAssistantVisible] = useState<boolean>(false);
  const [lastTutorPrompt, setLastTutorPrompt] = useState<string>("");
  const [isPromptInfoVisible, setIsPromptInfoVisible] = useState<boolean>(false);
  const [isFragmentTranslationVisible, setIsFragmentTranslationVisible] = useState<boolean>(false);
  const [graphNodeTooltip, setGraphNodeTooltip] = useState<GraphExample | null>(null);
  const [graphNodeTooltipTitle, setGraphNodeTooltipTitle] = useState<string>("");
  const [activeTheme, setActiveTheme] = useState<string>("");
  const [aiAssistantMode, setAiAssistantMode] = useState<AiAssistantMode>("idle");
  const [aiAssistantResponses, setAiAssistantResponses] = useState<AiAssistantResponses>({
    explanation: null,
    fragment_translation: null,
    response_translation: null,
    selection_explanation: null,
  });
  const [aiAssistantTargetLanguage, setAiAssistantTargetLanguage] = useState<string>(preferredLanguage);
  const [toolbarLayoutMode, setToolbarLayoutMode] = useState<ToolbarLayoutMode>(
    getToolbarLayoutMode(window.innerWidth)
  );
  const effectiveToolbarLayoutMode = isTestMode && previewWidth !== null ? getToolbarLayoutMode(previewWidth) : toolbarLayoutMode;
  const [isAiVerifyingForm, setIsAiVerifyingForm] = useState<boolean>(false);
  const [aiFormValidationResult, setAiFormValidationResult] = useState<AiValidationResponseDataDto | null>(null);
  const [hasManuallyVerifiedForm, setHasManuallyVerifiedForm] = useState(false);
  const aiVerificationGeneration = useRef(0);
  const aiVerificationBusy = useRef(false);
  useEffect(() => {
    const generationRef = aiVerificationGeneration;
    const busyRef = aiVerificationBusy;
    aiVerificationGeneration.current++;
    aiVerificationBusy.current = false;
    setIsAiVerifyingForm(false);
    setAiFormValidationResult(null);
    setHasManuallyVerifiedForm(false);
    setVerificationResults(null);
    return () => { generationRef.current++; busyRef.current = false; };
  }, [visible, fullModule?.module_key, selectedFragment.key]);

  const aiMenuRef = useRef<any>(null);
  const responsiveToolsMenuRef = useRef<any>(null);
  const actionsMenuRef = useRef<any>(null);
  const themeDropdownRef = useRef<Dropdown>(null);
  const toolbarMenuAnchorRef = useRef<HTMLElement | null>(null);
  const toggleToolbarMenu = (menu: React.RefObject<any>, event: React.MouseEvent<HTMLElement>) => {
    toolbarMenuAnchorRef.current = event.currentTarget;
    menu.current?.toggle(event);
  };
  const alignToolbarMenu = (menu: React.RefObject<any>) => {
    const element = menu.current?.getElement() as HTMLElement | null;
    const frame = toolbarMenuAnchorRef.current?.closest('.module-device-screen');
    if (!element || !frame) return;
    const bounds = frame.getBoundingClientRect();
    const position = element.getBoundingClientRect();
    const leftEdge = Math.max(0, bounds.left);
    const rightEdge = Math.min(window.innerWidth, bounds.right);
    const left = Math.max(leftEdge, Math.min(position.left, rightEdge - element.offsetWidth));
    element.style.left = `${parseFloat(element.style.left || '0') + left - position.left}px`;
  };
  const closeToolbarMenus = useCallback(() => {
    for (const menu of [aiMenuRef, responsiveToolsMenuRef, actionsMenuRef]) {
      if (menu.current?.getElement()) menu.current.hide();
    }
    if (themeDropdownRef.current?.getOverlay()) themeDropdownRef.current.hide();
  }, []);
  useEffect(() => { closeToolbarMenus(); }, [previewWidth, closeToolbarMenus]);
  const toolbarOverlayContainer = () => (document.fullscreenElement as HTMLElement | null) ?? document.body;

  useEffect(() => {
    setAiAssistantTargetLanguage(preferredLanguage);
  }, [preferredLanguage]);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  useEffect(() => {
    const handleResize = () => setToolbarLayoutMode(getToolbarLayoutMode(window.innerWidth));
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (fullModule?.fragments?.length) {
      const startKey = isTestMode ? initialFragmentKey : !isReviewMode && !startFromBeginning ? fullModule.resume_fragment_key : null;
      const start = startKey
        ? fullModule.fragments.find(fragment => fragment.key === startKey)
        : undefined;
      setSelectedFragment(start ?? fullModule.fragments[0]);
    }
  }, [fullModule, visible, isTestMode, isReviewMode, initialFragmentKey, startFromBeginning]);

  useEffect(() => {
    const availableThemes = fullModule?.theme ?? [];
    if (availableThemes.length > 0 && !activeTheme) {
      setActiveTheme(availableThemes[0].value);
    }
  }, [fullModule?.theme, activeTheme]);

  const onHtmlMessage = async (kind: string, data: any): Promise<void> => {
    if (kind === 'ai-selection-explain') {
        if (Config.showAiControls) await explainSelectionInContext(data);
        return;
    }

    if (data && typeof data.title === 'string' && data.title && typeof data.desc === 'string' && data.desc) {
      setGraphNodeTooltipTitle(`⚡ ${markdownText(data.title)}`);
      setGraphNodeTooltip({ title: data.title, desc: data.desc, hint: typeof data.hint === 'string' ? data.hint : undefined,
        items: isArray(data.items) ? data.items.filter((item: unknown) => typeof item === 'string') : [] });
    }
  };

  const generateAiResponse = async (
    request: AiRequestDto,
    cacheKey: string,
    isTranslation: boolean = false
  ): Promise<string | AiValidationResponseDataDto> => {
    if (USE_MOCK) {
      const mockText: string = await getNonStreamingResponse(isTranslation);
      setAiResponse(mockText);
      if (cacheKey) {
        setCacheValue(cacheKey, mockText);
      }
      return mockText;
    }

    try {
      if (isTranslation) {
        setIsTranslating(true);
        setAiResponse("Translating...");
      } else {
        setIsAiLoading(true);
        setAiResponse("");
      }

      if (!api) {
        const message = "API client is not available.";
        setAiResponse(message);
        return "";
      }

      const response: AiResponseDto | MessageDto = await api.askGenAi(request);

      if ("message" in response) {
        throw new Error(response.message);
      }

      setAiResponse(response.data);

      if ("prompt" in response && response.prompt) {
        setLastTutorPrompt(
          typeof response.prompt === "string"
            ? response.prompt
            : response.prompt?.text ?? JSON.stringify(response.prompt, null, 2)
        );
      }

      if (cacheKey) {
        if (typeof response.data === 'string' || response.data instanceof String) {
            setCacheValue(cacheKey, response.data as string);
        }
      }
      return response.data;
    } catch (e: any) {
      const message: string =
        e?.message ?? "Sorry, there was an error generating the response.";
      setAiResponse(message);
      return "";
    } finally {
      setIsAiLoading(false);
      setIsTranslating(false);
    }
  };

  const getAiAssistantEntryByMode = (mode: AiAssistantMode): AiAssistantEntry | null => {
    switch (mode) {
      case "explanation":
        return aiAssistantResponses.explanation;
      case "fragment_translation":
        return aiAssistantResponses.fragment_translation;
      case "response_translation":
        return aiAssistantResponses.response_translation;
      case 'selection_explanation':
        return aiAssistantResponses.selection_explanation;
      default:
        return null;
    }
  };

  const setAiAssistantEntryByMode = (
    mode: AiAssistantMode,
    entry: AiAssistantEntry
  ): void => {
    setAiAssistantResponses(prev => ({
      ...prev,
      ...(mode === "explanation" ? { explanation: entry } : {}),
      ...(mode === "fragment_translation" ? { fragment_translation: entry } : {}),
      ...(mode === "response_translation" ? { response_translation: entry } : {}),
      ...(mode === 'selection_explanation' ? { selection_explanation: entry } : {}),
    }));
  };

  const helpMeUnderstand = async (): Promise<void> => {
    setAiAssistantMode("explanation");
    if (USE_AI_ASSISTANT) {
      setIsAiAssistantVisible(true);
    } else {
      setIsAiDialogVisible(true);
    }
    setDisplayedLanguage(preferredLanguage);

    const cacheKey: string = `helpMeUnderstand_${selectedFragment.key}_${preferredLanguage}`;
    if (responseCache[cacheKey]) {
      setAiResponse(responseCache[cacheKey]);
      setAiAssistantEntryByMode("explanation", {
        content: responseCache[cacheKey],
        language: preferredLanguage
      });
      setDisplayedLanguage(preferredLanguage);
      return;
    }

    const request: AiRequestDto = {
      action: "help",
      html: renderSelectedFragment(),
      fragmentTitle: selectedFragment.title,
      authorHint: selectedFragment.aiHint,
      preferredLanguage,
      moduleKey: fullModule?.module_key,
      fragmentKey: selectedFragment.key,
      mode: "detailed"
    };

    const result = await generateAiResponse(request, cacheKey, false);

    if (result) {
      setAiAssistantEntryByMode("explanation", {
        content: result as string,
        language: preferredLanguage
      });
    }
  };

  const handleTranslate = async (targetLang: string) => {
    const explanationEntry = aiAssistantResponses.explanation;
    if (!explanationEntry?.content) return;
    if (targetLang === displayedLanguage && aiAssistantMode === "response_translation") return;

    const cacheKey: string = `translateExplanation_${selectedFragment.key}_${targetLang}`;

    setDisplayedLanguage(targetLang);
    setAiAssistantMode("response_translation");

    if (responseCache[cacheKey]) {
      const cached = responseCache[cacheKey];
      setAiResponse(cached);
      setAiAssistantEntryByMode("response_translation", {
        content: cached,
        language: targetLang
      });

      if (USE_AI_ASSISTANT) {
        setIsAiAssistantVisible(true);
      } else {
        setIsFragmentTranslationVisible(true);
      }
      return;
    }

    const request: AiRequestDto = {
      action: "translate",
      html: explanationEntry.content,
      targetLanguage: targetLang
    };

    const result = await generateAiResponse(request, cacheKey, true);

    if (result) {
      setAiAssistantEntryByMode("response_translation", {
        content: result as string,
        language: targetLang
      });
    }
  };

  const handleDirectTranslation = async (targetLang: string) => {
    const cacheKey: string = `translateFragment_${selectedFragment.key}_${targetLang}`;

    setDisplayedLanguage(targetLang);
    setAiAssistantMode("fragment_translation");

    if (responseCache[cacheKey]) {
      const cached = responseCache[cacheKey];
      setAiResponse(cached);
      setAiAssistantEntryByMode("fragment_translation", {
        content: cached,
        language: targetLang
      });

      if (USE_AI_ASSISTANT) {
        setIsAiAssistantVisible(true);
      } else {
        setIsFragmentTranslationVisible(true);
      }
      return;
    }

    const request: AiRequestDto = {
      action: "translate",
      html: renderSelectedFragment(),
      fragmentTitle: selectedFragment.title,
      authorHint: selectedFragment.aiHint,
      targetLanguage: targetLang,
      moduleKey: fullModule?.module_key,
      fragmentKey: selectedFragment.key
    };

    const result = await generateAiResponse(request, cacheKey, true);

    if (result) {
      setAiAssistantEntryByMode("fragment_translation", {
        content: result as string,
        language: targetLang
      });
    }

    if (USE_AI_ASSISTANT) {
      setIsAiAssistantVisible(true);
    } else {
      setIsFragmentTranslationVisible(true);
    }
  };

  const handleVerifyFormWithAi = async (results = verificationResults): Promise<void> => {
    const queryableResults = results?.filter(isAiQueryableResult) ?? [];
    if (queryableResults.length === 0 || aiVerificationBusy.current) {
        return;
    }
    const generation = aiVerificationGeneration.current;
    aiVerificationBusy.current = true;
    try {
        setIsAiVerifyingForm(true);

        const formFieldsForAi = queryableResults.map(r => ({
            fieldKey: r.field.key,
            label: r.field.label,
            type: r.field.type,
            userAnswer: readableAnswer(r.field, r.userAnswer),
            expectedAnswer: readableAnswer(r.field, r.expectedAnswer),
            tooltip: r.field.tooltip ?? "",
            aiHint: r.field.aiHint ?? "",
            weight: fieldWeight(r.field),
            ...(isMultiChoiceType(r.field.type) && Array.isArray(r.expectedAnswer) ? {
                selectedOptionKeys: selectionKeys(r.userAnswer), expectedOptionKeys: selectionKeys(r.expectedAnswer)
            } : {})
        }));

        const request: AiRequestDto = {
            action: "validate_form",
            html: renderSelectedFragment(),
            fragmentTitle: selectedFragment.title,
            authorHint: selectedFragment.aiHint,
            preferredLanguage,
            moduleKey: fullModule?.module_key,
            fragmentKey: selectedFragment.key,
            mode: "detailed",
            formFields: formFieldsForAi
        };

        const response = await api?.askGenAi(request);
        if (generation !== aiVerificationGeneration.current) return;
        if (response && isMessageDto(response)) {
            throw new Error(response.message);
        }
        if (response && isAiValidationResponseDto(response)) {
            setAiFormValidationResult(normalizeAiChoiceResults(queryableResults, response.data));
        } else throw new Error('AI verification returned an invalid response. Please try again.');
    } catch (e: any) {
        if (generation !== aiVerificationGeneration.current) return;
        const message =
            e?.response?.data?.message ?? e?.message ?? "AI verification failed. Please try again.";

        toastRef.current?.show({
            severity: "error",
            summary: "AI verification failed",
            detail: message,
            life: 4000
        });
    } finally {
        if (generation === aiVerificationGeneration.current) {
            aiVerificationBusy.current = false;
            setIsAiVerifyingForm(false);
        }
    }
  };

  const explainSelectionInContext = async (selectionContext: any): Promise<void> => {
    if (!selectionContext?.selectedText?.trim()) {
        return;
    }

    setAiAssistantMode('selection_explanation');

    if (USE_AI_ASSISTANT) {
        setIsAiAssistantVisible(true);
    } else {
        setIsAiDialogVisible(true);
    }

    setDisplayedLanguage(preferredLanguage);

    const request: AiRequestDto = {
        action: 'explain_selection',
        html: renderSelectedFragment(),
        fragmentTitle: selectedFragment.title,
        authorHint: selectedFragment.aiHint,
        preferredLanguage,
        moduleKey: fullModule?.module_key,
        fragmentKey: selectedFragment.key,
        mode: 'detailed',
        selectionContext: selectionContext
    };

    const result = await generateAiResponse(request, undefined!, false);

    if (result) {
        setAiAssistantEntryByMode('selection_explanation', {
            content: result as string,
            language: preferredLanguage
        });
    }
  };

  const hasAnswersForAiVerification = (): boolean => {
    if (!verificationResults || verificationResults.length === 0) {
        return false;
    }

    return verificationResults.some(r => isAiQueryableResult(r) && String(r.userAnswer ?? "").trim().length > 0);
  };

  const aiMenuItems = [
    {
      label: "Help me understand",
      icon: "pi pi-sparkles",
      command: async () => helpMeUnderstand()
    }
  ];

  if (preferredLanguage !== "English") {
    aiMenuItems.push({
      label: `Translate to ${preferredLanguage}`,
      icon: "pi pi-language",
      command: async () => handleDirectTranslation(preferredLanguage)
    });
  }

  aiMenuItems.push({
    label: "AI assistant",
    icon: "pi pi-bolt",
    command: async () => setIsAiAssistantVisible(true)
  });

  const onShow = () => {
    const container = dialogRef.current?.getElement() ?? null;
    dialogContainerRef.current = container;
  };

  const header = (
    <div className="module-preview-header flex align-items-center justify-content-between w-full">
      <div className="module-preview-caption text-lg font-semibold">
        <span>
        <ModuleLabel name={fullModule?.name ?? "Module details"} managed={fullModule?.is_managed} />
        {!(isTestMode && allowFragmentSelection) && selectedFragment.title ? ` - ${selectedFragment.title}` : ''}
        {isModuleReadOnly ? ' (Read-only review)' : isFragmentLocked ? ' (This form has already been submitted. Its answers cannot be changed)' : ''}
        </span>
        {isTestMode && allowFragmentSelection && <>
          <span aria-hidden="true">-</span>
          <Dropdown appendTo={toolbarOverlayContainer} aria-label="Preview fragment" className="module-preview-fragments"
            value={selectedFragment.key ?? null}
            options={(fullModule?.fragments ?? []).map((fragment, index) => ({
              value: fragment.key, label: `${index + 1}. ${fragment.title || 'Untitled fragment'}${fragment.hidden ? ' (hidden)' : ''}`
            }))}
            filter disabled={contentLoading || isSavingAnswers}
            panelStyle={{ maxWidth: 'calc(100vw - 2rem)' }}
            onChange={event => {
              const fragment = fullModule?.fragments?.find(candidate => candidate.key === event.value);
              if (fragment) void leaveAfterVerification(fragment, 'Preview fragment selector');
            }} />
        </>}
      </div>

      <div className="module-preview-header-actions">
        {isTestMode && <div className="module-preview-device-slot" ref={setPreviewControlsTarget} />}
        <Button
          aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
          icon={isFullscreen ? "pi pi-window-minimize" : "pi pi-window-maximize"}
          className="p-button-text p-button-rounded"
          onClick={() => {
            if (dialogContainerRef.current) {
              toggleFullscreen(dialogContainerRef.current);
            }
          }}
        />
      </div>
    </div>
  );

  const adjacentVisibleFragment = (fragment: ManagedFragmentDto, direction: -1 | 1): ManagedFragmentDto | undefined => {
    const fragments = fullModule?.fragments ?? [];
    const index = fragments.findIndex(candidate => candidate.key === fragment.key);
    if (index < 0) return undefined;
    // An author can jump directly to a hidden fragment; ordinary navigation still skips hidden pages.
    for (let next = index + direction; next >= 0 && next < fragments.length; next += direction) {
      if (!Boolean(fragments[next].hidden)) return fragments[next];
    }
    return undefined;
  };

  const isFirstFragment = (fragment: ManagedFragmentDto): boolean => {
    return !adjacentVisibleFragment(fragment, -1);
  };

  const isLastFragment = (fragment: ManagedFragmentDto): boolean => {
    return !adjacentVisibleFragment(fragment, 1);
  };

  const collectAndVerifyForm = (doVerification: boolean): [boolean, Record<string, any>] => {
    const values: Record<string, any> | null | undefined = formRef.current?.getValues();
    const updatedFildValues = {
      ...userInputsRef.current,
      [selectedFragment.key]: values ?? userInputsRef.current[selectedFragment.key] ?? {}
    };
    userInputsRef.current = updatedFildValues;
    setUserInputs(updatedFildValues);

    if (doVerification && !isAnswerReadOnly) {
      const errors: Record<string, string[]> | null | undefined = formRef.current?.validate();
      const message: string | null = buildErrorMessage(errors, selectedFragment.formFields, selectedFragment.html) ?? null;
      if (message) {
        setValidationMessage(message);
        setShowValidationErrorDialog(true);
        return [false, updatedFildValues[selectedFragment.key]];
      }
      return [true, updatedFildValues[selectedFragment.key]];
    } else {
      return [true, updatedFildValues[selectedFragment.key]];
    }
  };

  const postNavigationMessage = async (targetFragment: ManagedFragmentDto, viaCommand: string): Promise<void> => {
    if (!isTestMode) {
      const payload = {
        kind: "managedEvent",
        type: "navigation",
        from: selectedFragment.title,
        to: targetFragment.title,
        via: viaCommand
      };
      onMessage?.('navigation', payload);
    }
  };

  const postFormValidationMessage = async (
    verificationResults: UserAnswerVerificationResult[],
    viaCommand: string
  ): Promise<void> => {
    if (!isTestMode) {
      const results = verificationResults.map(r => {
        return {
          field: {
            key: r.field.key,
            label: r.field.label,
            ...(isMultiChoiceType(r.field.type) ? { type: r.field.type, options: r.field.options }
                : r.field.type === 'inline-text' ? { type: r.field.type } : {})
          },
          userAnswer: r.userAnswer,
          expectedAnswer: r.expectedAnswer,
          isCorrect: r.isCorrect
        };
      });

      const payload = {
        kind: "managedEvent",
        type: "formValidation",
        fragment: selectedFragment.title,
        fragment_key: selectedFragment.key,
        results: results,
        via: viaCommand
      };
      onMessage?.('formValidation', payload);
    }
  };

  const persistCurrentAnswers = async (values: Record<string, any>, leaving = false): Promise<boolean> => {
    if (isTestMode || !selectedFragment.key || isAnswerReadOnly) return true;
    const baseline = answerBaselinesRef.current[selectedFragment.key];
    // No loaded iframe means there are no learner edits to submit.
    if (!baseline) return true;
    const fillOnce = Boolean(selectedFragment.fillOnce);
    // Reviewing/verifying answers must not finalize a form before the learner leaves it.
    if (fillOnce && !leaving) return true;
    const snapshot = fieldAnswers(selectedFragment.formFields ?? [], values);
    const finalizing = fillOnce && Object.values(snapshot).some(hasFilledAnswer);
    const changes = finalizing ? snapshot : changedFieldAnswers(selectedFragment.formFields ?? [], baseline, values);
    if (Object.keys(changes).length === 0) return true;
    if (finalizing) {
      const confirmed = await new Promise<boolean>(resolve => {
        confirmationResolverRef.current = resolve;
        setSingleUseConfirmationVisible(true);
      });
      if (!confirmed) return false;
    }
    if (!api || !fullModule?.module_key) throw new Error('The API is not ready to save your answers.');
    setIsSavingAnswers(true);
    try {
      const result = finalizing
        ? await api.saveMyFragmentAnswers(fullModule.module_key, selectedFragment.key, changes, true)
        : await api.saveMyFragmentAnswers(fullModule.module_key, selectedFragment.key, changes);
      answerBaselinesRef.current[selectedFragment.key] = JSON.parse(JSON.stringify(values));
      if (result?.locked) setLockedFragments(previous => [...new Set([...previous, selectedFragment.key])]);
      return true;
    } catch (error: any) {
      // Another session may have finalized the form. Restore its authoritative answers before further navigation.
      if (error?.response?.data?.code === 'answers_locked') setAnswersRetry(value => value + 1);
      throw error;
    } finally {
      setIsSavingAnswers(false);
    }
  };

  const reportAnswerSaveError = (error: any) => {
    setAnswerSaveError(error?.response?.data?.message ?? error?.message ?? 'Unable to save your answers. Please try again.');
  };

  const handleClose = async (): Promise<void> => {
    if (navigationBusyRef.current) return;
    navigationBusyRef.current = true;
    try {
      if (!answersLoading && !contentLoading) {
        const [, values] = collectAndVerifyForm(false);
        if (!await persistCurrentAnswers(values, true)) return;
      }
      onClose();
    } catch (error) {
      reportAnswerSaveError(error);
    } finally {
      navigationBusyRef.current = false;
    }
  };

  const handleActionClick = async (action: FragmentActionDto): Promise<void> => {
    if (navigationBusyRef.current || answersLoading || contentLoading) return;
    if (action.type === 'compareAnswers') {
      if (isTestMode || !availableComparisons.length) return;
      const [, values] = collectAndVerifyForm(false);
      setComparisonValues(fieldAnswers(selectedFragment.formFields ?? [], values));
      setComparisonVisible(true);
      return;
    }
    if (action.type === 'resetForm') {
      if (!isAnswerReadOnly) formRef.current?.resetValues();
      return;
    }
    navigationBusyRef.current = true;
    try {
      const [isValid, formFields] = collectAndVerifyForm(action.type !== 'gotoPrevious' && action.type !== 'showForm');
      if (!isValid) return;
      const leaving = (action.type === 'gotoNext' && !isLastFragment(selectedFragment))
        || (action.type === 'gotoPrevious' && !isFirstFragment(selectedFragment))
        || (action.type === 'gotoFragment' && action.to !== selectedFragment.key
          && Boolean(fullModule?.fragments?.some(fragment => fragment.key === action.to)));
      if (!await persistCurrentAnswers(formFields, leaving)) return;

      switch (action.type) {
        case "gotoNext":
          if (!isLastFragment(selectedFragment)) {
            const fragment = adjacentVisibleFragment(selectedFragment, 1)!;
            setSelectedFragment(fragment);
            postNavigationMessage(fragment, action.label);
          }
          break;

        case "gotoPrevious":
          if (!isFirstFragment(selectedFragment)) {
            const fragment = adjacentVisibleFragment(selectedFragment, -1)!;
            setSelectedFragment(fragment);
            postNavigationMessage(fragment, action.label);
          }
          break;

        case "gotoFragment": {
          const fragment = fullModule?.fragments?.find(
            (f: ManagedFragmentDto) => f.key === action.to
          );
          if (fragment) {
            setSelectedFragment(fragment);
            postNavigationMessage(fragment, action.label);
          }
          break;
        }

        case "validateForm": {
          const results = verifyUserAnswers(
            selectedFragment.formFields,
            formFields
          );
          const aiResults = includeFreeTextResults(results, selectedFragment.formFields, formFields);
          setVerificationResults(aiResults);
          setVerificationResultsTo(action.to);
          setVerificationResultsPayload(action.payload);
          postFormValidationMessage(results, action.label);
          setVerificationAction(action);
          aiVerificationGeneration.current++;
          aiVerificationBusy.current = false;
          setAiFormValidationResult(null);
          setHasManuallyVerifiedForm(false);
          setIsAiVerifyingForm(false);
          if (needsAutomaticAiVerification(aiResults)) void handleVerifyFormWithAi(aiResults);
          break;
        }

        case "showForm":
          setVerificationResults(verifyUserAnswers(selectedFragment.formFields, formFields));
          setVerificationAction(action);
          break;
      }
    } catch (error) {
      reportAnswerSaveError(error);
    } finally {
      navigationBusyRef.current = false;
    }
  };

  const leaveAfterVerification = async (fragment: ManagedFragmentDto, via: string): Promise<void> => {
    if (navigationBusyRef.current || fragment.key === selectedFragment.key) return;
    navigationBusyRef.current = true;
    try {
      const [, values] = collectAndVerifyForm(false);
      if (!await persistCurrentAnswers(values, true)) return;
      setSelectedFragment(fragment);
      postNavigationMessage(fragment, via);
    } catch (error) {
      reportAnswerSaveError(error);
    } finally {
      navigationBusyRef.current = false;
    }
  };

  const isFragmentActionDisabled = (action: FragmentActionDto): boolean =>
    (isTestMode && action.type === 'compareAnswers') || (isAnswerReadOnly && action.type === 'resetForm');

  const getVisibleFragmentActions = (): FragmentActionDto[] => {
    return (selectedFragment.actions ?? []).filter(action => action.type !== 'compareAnswers' || isTestMode || availableComparisons.length > 0).sort(
      (a: FragmentActionDto, b: FragmentActionDto) => a.order - b.order
    );
  };

  const getPrimaryNavigationActions = (): FragmentActionDto[] => {
    const isFirst: boolean = isFirstFragment(selectedFragment);
    const isLast: boolean = isLastFragment(selectedFragment);

    return getVisibleFragmentActions().filter((a: FragmentActionDto) => {
      if (a.type === "gotoPrevious") return !isFirst;
      if (a.type === "gotoNext") return !isLast;
      return false;
    });
  };

  const getSecondaryFragmentActions = (): FragmentActionDto[] => {
    return getVisibleFragmentActions().filter((a: FragmentActionDto) => {
      return a.type !== "gotoPrevious" && a.type !== "gotoNext";
    });
  };

  const getActionIconClass = (action: FragmentActionDto): string => {
    switch (action.type) {
      case "gotoNext":
        return "pi pi-arrow-right";
      case "gotoPrevious":
        return "pi pi-arrow-left";
      case "gotoFragment":
        return "pi pi-compass";
      case "validateForm":
        return "pi pi-check-square";
      case "showForm":
        return "pi pi-eye";
      case "resetForm":
        return "pi pi-refresh";
      case "compareAnswers":
        return "pi pi-chart-line";
      default:
        return "pi pi-cog";
    }
  };

  const generateActions = (): JSX.Element => {
    const isFirst: boolean = isFirstFragment(selectedFragment);
    const isLast: boolean = isLastFragment(selectedFragment);
    const actions = getVisibleFragmentActions();

    return (
      <>
        {actions.map(a => {
          const iconClass: string =
            a.type === "gotoNext" ? " pi-arrow-right"
              : a.type === "gotoPrevious" ? " pi-arrow-left"
                : a.type === "gotoFragment" ? " pi-compass"
                  : a.type === "validateForm" ? " pi-check-square"
                    : a.type === "showForm" ? " pi-eye"
                      : a.type === "resetForm" ? " pi-refresh"
                        : a.type === "compareAnswers" ? " pi-chart-line"
                        : "";

          const isNext: boolean = a.type === "gotoNext";
          const isPrev: boolean = a.type === "gotoPrevious";
          const generateButton: boolean = isNext ? !isLast : isPrev ? !isFirst : true;

          return (
            <>
              {generateButton && (
                <Button
                  label={a.label}
                  disabled={isFragmentActionDisabled(a)}
                  icon={`pi ${iconClass}`}
                  onClick={() => handleActionClick(a)}
                  className="p-button-secondary"
                  style={{ marginRight: "1rem" }}
                />
              )}
            </>
          );
        })}
      </>
    );
  };

  const responsiveToolsMenuItems = [
    {
      label: "Instructions",
      icon: "pi pi-info-circle",
      command: () => setInstructionsVisible(true)
    },
    ...(Config.showAiControls ? [
    {
      label: "AI",
      icon: "pi pi-bolt",
      command: () => {
        if (USE_AI_ASSISTANT) {
          setIsAiAssistantVisible(true);
        } else {
          helpMeUnderstand();
        }
      }
    }
    ] : []),
    ...(fullModule?.theme ?? []).map((themeOption: any) => ({
      label: `Theme: ${themeOption.label}`,
      icon: "pi pi-palette",
      style: { whiteSpace: 'nowrap' },
      command: () => setActiveTheme(themeOption.value)
    }))
  ];

  const renderResponsiveLeftToolbar = (): JSX.Element => {
    return (
      <div className="flex align-items-center gap-2">
        <TieredMenu
          appendTo={toolbarOverlayContainer}
          model={responsiveToolsMenuItems}
          popup
          ref={responsiveToolsMenuRef} transitionOptions={{ timeout: 120, onEntering: () => alignToolbarMenu(responsiveToolsMenuRef), onEntered: () => alignToolbarMenu(responsiveToolsMenuRef) }}
          id="responsive_tools_menu"
          style={{ border: '1px dotted' }} 
        />
        <Button
          type="button"
          icon="pi pi-bars"
          className="p-button-secondary responsive-toolbar-btn"
          onClick={(e) => toggleToolbarMenu(responsiveToolsMenuRef, e)}
          aria-controls="responsive_tools_menu"
          aria-haspopup
        />
      </div>
    );
  };

  const renderMobileRightToolbar = (): JSX.Element => {
    const primaryActions = getPrimaryNavigationActions();

    const prevAction = primaryActions.find((a: FragmentActionDto) => a.type === "gotoPrevious");
    const nextAction = primaryActions.find((a: FragmentActionDto) => a.type === "gotoNext");

    const mobileMenuItems = [
      ...getSecondaryFragmentActions().map((action: FragmentActionDto) => ({
        label: action.label,
        icon: getActionIconClass(action),
        disabled: isFragmentActionDisabled(action),
        command: () => handleActionClick(action)
      })),
      {
        label: "Close",
        icon: "pi pi-times",
        command: () => handleClose()
      }
    ];

    return (
      <div className="flex align-items-center gap-2">
        {prevAction && (
          <Button
            label={prevAction.label}
            icon="pi pi-arrow-left"
            onClick={() => handleActionClick(prevAction)}
            className="p-button-secondary mobile-toolbar-btn"
          />
        )}

        {nextAction && (
          <Button
            label={nextAction.label}
            icon="pi pi-arrow-right"
            iconPos="right"
            onClick={() => handleActionClick(nextAction)}
            className="p-button-secondary mobile-toolbar-btn"
          />
        )}

        <TieredMenu
          appendTo={toolbarOverlayContainer}
          model={mobileMenuItems}
          popup
          ref={actionsMenuRef} transitionOptions={{ timeout: 120, onEntering: () => alignToolbarMenu(actionsMenuRef), onEntered: () => alignToolbarMenu(actionsMenuRef) }}
          id="actions_menu"
        />
        <Button
          type="button"
          icon="pi pi-ellipsis-v"
          aria-label="More actions"
          className="p-button-secondary mobile-toolbar-btn"
          onClick={(e) => toggleToolbarMenu(actionsMenuRef, e)}
          aria-controls="actions_menu"
          aria-haspopup
        />
      </div>
    );
  };

  const renderCompactRightToolbar = (): JSX.Element => {
    const primaryActions = getPrimaryNavigationActions();
    const secondaryActions = getSecondaryFragmentActions();

    const prevAction = primaryActions.find((a: FragmentActionDto) => a.type === "gotoPrevious");
    const nextAction = primaryActions.find((a: FragmentActionDto) => a.type === "gotoNext");

    const visibleExtraActions = secondaryActions.slice(0, 2);
    const overflowExtraActions = secondaryActions.slice(2);

    const compactMenuItems = [
      ...overflowExtraActions.map((action: FragmentActionDto) => ({
        label: action.label,
        icon: getActionIconClass(action),
        disabled: isFragmentActionDisabled(action),
        command: () => handleActionClick(action)
      })),
      {
        label: "Close",
        icon: "pi pi-times",
        command: () => handleClose()
      }
    ];

    return (
      <div className="flex align-items-center gap-2 flex-wrap justify-content-end">
        {prevAction && (
          <Button
            label={prevAction.label}
            icon="pi pi-arrow-left"
            onClick={() => handleActionClick(prevAction)}
            className="p-button-secondary compact-toolbar-btn"
          />
        )}

        {nextAction && (
          <Button
            label={nextAction.label}
            icon="pi pi-arrow-right"
            iconPos="right"
            onClick={() => handleActionClick(nextAction)}
            className="p-button-secondary compact-toolbar-btn"
          />
        )}

        {visibleExtraActions.map((action: FragmentActionDto) => (
          <Button
            key={`${action.type}_${action.label}_${action.order}`}
            label={action.label}
            disabled={isFragmentActionDisabled(action)}
            icon={getActionIconClass(action)}
            onClick={() => handleActionClick(action)}
            className="p-button-secondary compact-toolbar-btn"
          />
        ))}

        <TieredMenu
          appendTo={toolbarOverlayContainer}
          model={compactMenuItems}
          popup
          ref={actionsMenuRef} transitionOptions={{ timeout: 120, onEntering: () => alignToolbarMenu(actionsMenuRef), onEntered: () => alignToolbarMenu(actionsMenuRef) }}
          id="actions_menu"
        />
        <Button
          type="button"
          icon="pi pi-ellipsis-v"
          aria-label="More actions"
          className="p-button-secondary compact-toolbar-btn"
          onClick={(e) => toggleToolbarMenu(actionsMenuRef, e)}
          aria-controls="actions_menu"
          aria-haspopup
        />
      </div>
    );
  };

  const renderResponsiveToolbar = (): JSX.Element => {
    return (
      <div
        className="flex align-items-center justify-content-between mt-4"
        style={{ marginTop: "auto", gap: "0.5rem" }}
      >
        {renderResponsiveLeftToolbar()}
        {effectiveToolbarLayoutMode === "mobile" ? renderMobileRightToolbar() : renderCompactRightToolbar()}
      </div>
    );
  };

  const renderDesktopToolbar = (): JSX.Element => {
    return (
      <div className="flex align-items-center mt-4" style={{ marginTop: "auto" }}>
        <div className="flex flex-1">
          {((fullModule?.instructions ?? "").length > 0 || (selectedFragment.instructions ?? "").length > 0) && (
            <>
              <Button
                label="Instructions"
                icon="pi pi-info-circle"
                onClick={() => setInstructionsVisible(true)}
                className="p-button-secondary"
              />&nbsp;
            </>
          )}

          {Config.showAiControls && <>
          <TieredMenu appendTo={toolbarOverlayContainer} model={aiMenuItems} popup ref={aiMenuRef} transitionOptions={{ timeout: 120, onEntering: () => alignToolbarMenu(aiMenuRef), onEntered: () => alignToolbarMenu(aiMenuRef) }} id="ai_menu" style={{ whiteSpace: "nowrap" }} />
          {USE_AI_ASSISTANT ? (
            <Button
              type="button"
              icon="pi pi-bolt"
              label="AI"
              severity="help"
              onClick={() => setIsAiAssistantVisible(true)}
            />
          ) : (
            <Button
              type="button"
              icon="pi pi-bolt"
              label="AI"
              severity="help"
              onClick={(e) => toggleToolbarMenu(aiMenuRef, e)}
              aria-controls="ai_menu"
              aria-haspopup
            />
          )}
          </>}

          {(fullModule?.theme?.length ?? 0) > 0 && (
            <Dropdown ref={themeDropdownRef}
              appendTo={toolbarOverlayContainer}
              value={activeTheme}
              options={fullModule?.theme ?? []}
              optionLabel="label"
              optionValue="value"
              onChange={(e) => setActiveTheme(e.value)}
              placeholder="No theme selected"
              className="w-12rem ml-3"
            />
          )}
        </div>

        <div className="flex justify-content-end flex-1">
          {generateActions()}
          <Button
            label="Close"
            icon="pi pi-times"
            onClick={() => handleClose()}
            className="p-button-secondary"
          />
        </div>
      </div>
    );
  };

  const inputValues: Record<string, any> = userInputs[selectedFragment.key];
  const currentAiAssistantEntry = getAiAssistantEntryByMode(aiAssistantMode);

  return (
    <>
      <Dialog
        ref={dialogRef}
        header={header}
        visible={visible}
        onShow={onShow}
        onHide={() => handleClose()}
        modal
        draggable={false}
        baseZIndex={10000}
        style={{
          width: "95vw",
          maxWidth: "2100px",
          height: "99vh",
          display: "flex",
          flexDirection: "column"
        }}
        breakpoints={{
          "1400px": "95vw",
          "1200px": "95vw",
          "960px": "95vw",
          "640px": "100vw"
        }}
        contentStyle={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden"
        }}
        transitionOptions={{ classNames: "p-dialog", timeout: 300 }}
      >
        <Toast ref={toastRef} appendTo={dialogContainerRef.current ?? undefined} />
        <CompareAnswersDialog visible={comparisonVisible && visible} comparisons={availableComparisons}
          currentAnswers={comparisonValues} parent={dialogContainerRef.current} onClose={() => setComparisonVisible(false)} />
        {comparisonLoading && <small role="status">Loading answer comparisons…</small>}
        {comparisonError && <div role="alert">{comparisonError} <Button label="Retry loading comparisons" onClick={() => setComparisonRetry(value => value + 1)} /></div>}
        <Dialog header="Save permanent answers?" visible={singleUseConfirmationVisible}
          onHide={() => resolveSingleUseConfirmation(false)} modal baseZIndex={11000}
          appendTo={dialogContainerRef.current ?? undefined} style={{ width: 'min(32rem, 95vw)' }}>
          <p>When you leave this fragment, these answers will be saved permanently. You will not be able to change any field in this form during this or a future session.</p>
          <div className="flex justify-content-end gap-2">
            <Button label="Cancel" className="p-button-secondary" onClick={() => resolveSingleUseConfirmation(false)} />
            <Button label="Save and continue" onClick={() => resolveSingleUseConfirmation(true)} />
          </div>
        </Dialog>
        <GeneralErrorDialog
          parent={dialogContainerRef.current}
          visible={answerSaveError.length > 0}
          title="Unable to save answers"
          message={answerSaveError}
          details="Your answers remain open. Please try navigating or closing the module again."
          onClose={() => setAnswerSaveError('')}
        />
        {(contentLoading || answersLoading) && (
          <>
            <div className="flex flex-column justify-content-center align-items-center h-screen gap-3">
              {answersLoadError ? <>
                <span role="alert">{answersLoadError}</span>
                <Button label="Retry loading answers" onClick={() => setAnswersRetry(value => value + 1)} />
                <Button label="Close" onClick={() => handleClose()} />
              </> : <>
                <ProgressSpinner />
                <span className="text-xl text-primary">Loading module content and answers...</span>
              </>}
            </div>
          </>
        )}

        {!contentLoading && !answersLoading && (
          <ModulePreviewViewport enabled={isTestMode} onWidthChange={setPreviewWidth} controlsTarget={previewControlsTarget}
            onViewportScroll={closeToolbarMenus}
            footer={effectiveToolbarLayoutMode === "desktop" ? renderDesktopToolbar() : renderResponsiveToolbar()}>
            <div className="entity-list-container">
              {isSavingAnswers && <span role="status">Saving answers...</span>}
              <div className="entity-list-wrapper" inert={isSavingAnswers ? true : undefined} aria-busy={isSavingAnswers}>
                <HtmlViewer
                  key={selectedFragment.key}
                  htmlContent={renderSelectedFragment()}
                  theme={activeTheme}
                  instructions={selectedFragment.instructions ?? ""}
                  onMessage={(kind, data) => {
                    onHtmlMessage?.(kind, data);
                  }}
                  selectedMode={"preview"}
                  readOnly={true}
                  hideControls={true}
                  sandboxStyles={false}
                  ref={formRef}
                  inputValues={inputValues}
                  inputsReadOnly={isAnswerReadOnly}
                  onValuesReady={values => {
                    if (visible && !contentLoading && !isTestMode && !isModuleReadOnly && fullModule?.is_managed
                        && api && moduleKey && selectedFragment.key) {
                      recordPosition(selectedFragment.key);
                      onPositionViewed?.(selectedFragment.key);
                    }
                    if (!answerBaselinesRef.current[selectedFragment.key]) {
                      answerBaselinesRef.current[selectedFragment.key] = JSON.parse(JSON.stringify(values));
                    }
                  }}
                />
              </div>
            </div>
          </ModulePreviewViewport>
        )}

        {!contentLoading && !answersLoading && (
          <>
            <InstructionsDialog
              parent={dialogContainerRef.current}
              visible={instructionsVisible}
              onHide={() => setInstructionsVisible(false)}
              instructions={[fullModule?.instructions ?? "", selectedFragment.instructions ?? ""]
                .map(instructions => instructions.trim())
                .filter(Boolean)}
            />

            <GeneralErrorDialog
              parent={dialogContainerRef.current}
              visible={showValidationErrorDialog}
              title={"Input validation errors"}
              message={validationMessage}
              details={"Some inputs contain invalid values or missing please review the message above and provide valid values for input elements."}
              onClose={() => setShowValidationErrorDialog(false)}
            />

            <AIExplainDialog
              visible={Config.showAiControls && isAiDialogVisible}
              onHide={() => {
                setIsAiDialogVisible(false);
                setAiResponse("");
              }}
              content={aiResponse as string}
              isAiLoading={isAiLoading}
              parent={dialogContainerRef.current}
              onTranslate={(targetLang: string) => handleTranslate(targetLang)}
              isTranslating={isTranslating}
              displayedLanguage={displayedLanguage}
              onShowPrompt={() => isTestMode ? setIsPromptInfoVisible(true) : undefined}
            />

            <GeneralInfoDialog
              parent={dialogContainerRef.current}
              visible={Config.showAiControls && isPromptInfoVisible}
              title="Raw AI tutor prompt"
              message={`<pre style="white-space: pre-wrap; font-family: monospace;">${lastTutorPrompt}</pre>`}
              onClose={() => setIsPromptInfoVisible(false)}
              width={`90%`}
            />

            <GeneralInfoDialog
              parent={dialogContainerRef.current}
              visible={Config.showAiControls && isFragmentTranslationVisible}
              title="Translated text"
              message={aiResponse as string}
              onClose={() => setIsFragmentTranslationVisible(false)}
              width={`90%`}
            />

            <GeneralInfoDialog
              parent={dialogContainerRef.current}
              visible={graphNodeTooltip !== null}
              title={graphNodeTooltipTitle}
              message=""
              onClose={() => setGraphNodeTooltip(null)}
            >
              {graphNodeTooltip && <GraphExampleContent example={graphNodeTooltip} />}
            </GeneralInfoDialog>

            <FormVerificationDialog
              parent={dialogContainerRef.current}
              visible={verificationResults != null}
              results={verificationResults!}
              showUserResults={verificationAction?.type === "validateForm"}
              answersExplanation={selectedFragment.answers_explanation}
              fragmentHtml={selectedFragment.html}
              formFields={selectedFragment.formFields}
              isAiVerifying={isAiVerifyingForm}
              onVerifyWithAi={ Config.showAiControls && verificationAction?.type === "validateForm" && hasAnswersForAiVerification() ? () => {
                if (aiVerificationBusy.current) return;
                setHasManuallyVerifiedForm(true);
                void handleVerifyFormWithAi();
              } : undefined }
              aiValidationResult={aiFormValidationResult}
              showAiColumn={hasManuallyVerifiedForm}
              isAiVerificationAvailable={!hasManuallyVerifiedForm || !aiFormValidationResult}
              onClose={() => {
                aiVerificationGeneration.current++;
                aiVerificationBusy.current = false;
                setIsAiVerifyingForm(false);
                setVerificationResults(null);
                setAiFormValidationResult(null);
                setHasManuallyVerifiedForm(false);
                if (verificationAction?.type === "validateForm") {
                  let targetFragmentKey: string | null = null;
                  if (verificationResultsPayload) {
                    const hasIncorrect: boolean = verificationResults?.some(r => hasExpectedAnswer(r.expectedAnswer, r.field.type) && r.isCorrect === false) ?? false;
                    if (hasIncorrect) {
                      targetFragmentKey = verificationResultsPayload.toOnIncorrect ?? null;
                    } else {
                      targetFragmentKey = verificationResultsPayload.toOnAllCorrect ?? null;
                    }
                  }
                  if (!targetFragmentKey && verificationResultsTo) {
                    targetFragmentKey = verificationResultsTo;
                  }
                  const fragment: ManagedFragmentDto | undefined = fullModule?.fragments?.find(
                    (f: ManagedFragmentDto) => f.key === targetFragmentKey
                  );
                  if (fragment) {
                    leaveAfterVerification(fragment, verificationAction?.label ?? "");
                  }
                }
              }}
            />

            <AiWorkspaceDialog
              parent={dialogContainerRef.current}
              visible={Config.showAiControls && isAiAssistantVisible}
              onHide={() => {
                setIsAiAssistantVisible(false);
                setAiResponse("");
              }}
              mode={aiAssistantMode}
              response={currentAiAssistantEntry?.content ?? ""}
              displayedLanguage={currentAiAssistantEntry?.language ?? displayedLanguage}
              isLoading={isAiLoading}
              preferredLanguage={preferredLanguage}
              onExplain={() => helpMeUnderstand()}
              onShowPrompt={() => isTestMode ? setIsPromptInfoVisible(true) : undefined}
              canTranslateResponse={!!aiAssistantResponses.explanation?.content}
              isTranslating={isTranslating}
              targetLanguage={aiAssistantTargetLanguage}
              onTargetLanguageChange={setAiAssistantTargetLanguage}
              availableLanguages={EUROPEAN_LANGUAGES}
              onTranslateFragment={(lang: string) => handleDirectTranslation(lang)}
              onTranslateResponse={(lang: string) => handleTranslate(lang)}
            />
          </>
        )}
      </Dialog>
    </>
  );
}
